// Number Line Jumper: walk along a number line and hop onto the right tick.
// Wrong hops bounce gently and say which way to go.

import { h, clear } from '@kit/dom';
import { makeSurface } from '@kit/canvas';
import { makeLoop } from '@kit/loop';
import { mapFromStrings, moveBody, drawMap, followCamera, makePad, type Body, type Camera } from '@kit/tilemap';
import { drawSprite, type Facing } from '@avatar/render';
import type { GameModule, GameContext } from '@shell/types';
import { makeJump, specFor, tickCount, tickValue, tickIndex, fmtValue, type JumpProblem, type LineSpec } from '@problems/number-line';

const T = 24;              // tile px
const ROWS = 7;
const TICK_W = 2;          // tiles between ticks
const MARGIN = 2;          // tiles before the first tick
const GRAVITY = 980, HOP = -300, RUN = 120;
const JUMPS_PER_ROUND = 8;

function mount(host: HTMLElement, ctx: GameContext) {
  let level = ctx.currentLevel();
  let spec: LineSpec = specFor(level, ctx.rng);
  let map = buildMap(spec);
  let problem: JumpProblem = makeJump(level, ctx.rng, spec);
  let n = 0;
  let attempts = 0, correct = 0, misses = 0;
  let hinted = false;
  let hopping = false;      // a scored hop is in the air
  let litTicks = new Set<number>();
  let flash: { x: number; good: boolean; until: number } | null = null;
  let finished = false;
  const t0 = performance.now();
  let tProblem = performance.now();

  const body: Body = { x: 0, y: 0, w: 13, h: 24, vx: 0, vy: 0 };
  let facing: Facing = 'right';
  let walkT = 0;

  const head = h('div.row', { style: { justifyContent: 'space-between' } }, h('span.muted', null, 'Jump ', h('strong', null, '1'), ` of ${JUMPS_PER_ROUND}`), h('button.btn.small.ghost', { onclick: hint }, 'Hint'));
  const prompt = h('p.center', { style: { minHeight: '48px', fontWeight: '800', fontSize: '19px', padding: '2px 4px' } });
  const stage = h('div');
  host.append(head, prompt, stage);
  const S = makeSurface(stage, { height: ROWS * T, draw: false });
  const pad = makePad(host, { action: 'Jump!' });
  const cam: Camera = { x: 0, y: 0, w: S.w, h: ROWS * T };
  let padWasDown = false;

  function buildMap(s: LineSpec) {
    const cols = MARGIN * 2 + (tickCount(s) - 1) * TICK_W + 1;
    const rows: string[] = [];
    for (let r = 0; r < ROWS; r++) rows.push((r >= ROWS - 2 ? '#' : '.').repeat(cols));
    return mapFromStrings(rows, { '.': 0, '#': 1 }, T, [1]);
  }
  function tickX(i: number): number { return (MARGIN + i * TICK_W) * T + T / 2; }
  function placeAt(v: number) {
    const i = Math.max(0, Math.min(tickCount(spec) - 1, tickIndex(spec, v)));
    body.x = tickX(i) - body.w / 2; body.y = (ROWS - 2) * T - body.h - 1; body.vx = 0; body.vy = 0;
    cam.x = Math.max(0, body.x - cam.w / 2);
  }

  function newProblem() {
    level = ctx.currentLevel();
    const nextSpec = specFor(level, ctx.rng);
    if (nextSpec.min !== spec.min || nextSpec.max !== spec.max || nextSpec.step !== spec.step) { spec = nextSpec; map = buildMap(spec); litTicks = new Set(); }
    problem = makeJump(level, ctx.rng, spec);
    hinted = false; misses = 0; tProblem = performance.now();
    prompt.textContent = problem.prompt;
    (head.querySelector('strong') as HTMLElement).textContent = String(n + 1);
    if (problem.from !== undefined) placeAt(problem.from);
    else if (n === 0) placeAt(spec.min);
    ctx.say(problem.from !== undefined ? `You are standing on ${fmtValue(problem.from, spec.labels)}.` : 'Walk with the arrows, then press Jump! on the number.');
  }

  function nearestTick(): { i: number; dist: number } {
    const cx = body.x + body.w / 2;
    const i = Math.round((cx - T / 2) / T / TICK_W - MARGIN / TICK_W);
    const ii = Math.max(0, Math.min(tickCount(spec) - 1, i));
    return { i: ii, dist: Math.abs(cx - tickX(ii)) };
  }

  function land() {
    hopping = false;
    const { i, dist } = nearestTick();
    const ms = performance.now() - tProblem;
    if (dist > T * 0.6) { ctx.say('Land right on a tick mark.'); return; }
    const v = tickValue(spec, i);
    attempts++;
    if (Math.abs(v - problem.target) < 1e-9) {
      correct++;
      litTicks.add(i);
      flash = { x: tickX(i), good: true, until: performance.now() + 600 };
      ctx.report({ skill: problem.skill, level, correct: true, hintUsed: hinted, ms, problemId: `jump:${problem.kind}:${problem.target}` });
      ctx.sound('ding');
      ctx.say(`${fmtValue(v, spec.labels)}! That is it.`);
      n++;
      if (n >= JUMPS_PER_ROUND) { finish(); return; }
      setTimeout(newProblem, 900);
    } else {
      misses++;
      flash = { x: tickX(i), good: false, until: performance.now() + 500 };
      ctx.report({ skill: problem.skill, level, correct: false, hintUsed: hinted, ms, problemId: `jump:${problem.kind}:${problem.target}` });
      ctx.sound('buzz');
      const ticksOff = Math.abs(tickIndex(spec, problem.target) - i);
      const dir = problem.target > v ? 'right' : 'left';
      ctx.say(`${fmtValue(v, spec.labels)} is not it. Go ${dir} ${ticksOff} tick${ticksOff === 1 ? '' : 's'}.`);
      body.vx = dir === 'right' ? 60 : -60; body.vy = -120; // gentle bounce toward the answer
      if (misses >= 2) hint();
    }
  }

  function hint() {
    if (finished) return;
    hinted = true;
    ctx.say(`Hint: ${problem.hint}`);
  }

  function finish() {
    finished = true;
    ctx.sound('chime');
    if (attempts === correct && level >= 5) ctx.charm('charm.jumper');
    setTimeout(() => ctx.end({ attempts, correct, perfect: attempts === correct, durationMs: performance.now() - t0 }), 800);
  }

  function update(dt: number) {
    if (finished) return;
    const want = (pad.right ? 1 : 0) - (pad.left ? 1 : 0);
    if (!hopping) body.vx = want * RUN;
    if (want) facing = want > 0 ? 'right' : 'left';
    if (pad.action && !padWasDown && body.onGround && !hopping) { hopping = true; body.vy = HOP; body.vx = 0; ctx.sound('tick'); }
    padWasDown = pad.action;
    body.vy += GRAVITY * dt;
    if (body.vy > 600) body.vy = 600;
    const wasOnGround = body.onGround;
    moveBody(map, body, dt);
    if (hopping && !wasOnGround && body.onGround) land();
    walkT = want && !hopping ? walkT + dt * 8 : 0;
    cam.w = S.w;
    followCamera(cam, map, body.x + body.w / 2, ROWS * T / 2, 0.2);
  }

  function draw() {
    const c = S.ctx, W = S.w, H = S.h;
    c.fillStyle = '#bfe3f5'; c.fillRect(0, 0, W, H);
    c.fillStyle = '#8fd08a';
    for (let i = -1; i < 6; i++) { const hx = i * 160 - (cam.x * 0.3) % 160; c.beginPath(); c.ellipse(hx, H * 0.74, 110, 36, 0, 0, 7); c.fill(); }
    drawMap(c, map, cam, (id, sx, sy, size) => {
      if (id === 1) { c.fillStyle = '#9ad67e'; c.fillRect(sx, sy, size, size); c.fillStyle = '#8a5a3b'; c.fillRect(sx, sy + 6, size, size - 6); }
    });
    // ticks and labels on the ground
    const groundY = (ROWS - 2) * T - cam.y;
    const nTicks = tickCount(spec);
    c.textAlign = 'center'; c.textBaseline = 'top';
    for (let i = 0; i < nTicks; i++) {
      const x = tickX(i) - cam.x;
      if (x < -40 || x > W + 40) continue;
      const v = tickValue(spec, i);
      const labeled = Math.abs(v / spec.labelEvery - Math.round(v / spec.labelEvery)) < 1e-9;
      const lit = litTicks.has(i);
      const isFrom = problem.from !== undefined && Math.abs(v - problem.from) < 1e-9;
      c.fillStyle = lit ? '#5fbf6a' : isFrom ? '#f5c542' : '#2a2a2e';
      c.fillRect(x - 1.5, groundY - (labeled ? 14 : 8), 3, labeled ? 14 : 8);
      if (labeled || nTicks <= 12) {
        c.fillStyle = lit ? '#2e7d4f' : '#2a2a2e';
        c.font = `800 ${nTicks > 30 ? 11 : 13}px Nunito, sans-serif`;
        c.fillText(fmtValue(v, spec.labels), x, groundY + 8);
      }
    }
    if (flash && performance.now() < flash.until) {
      c.strokeStyle = flash.good ? '#5fbf6a' : '#e2574c'; c.lineWidth = 3;
      c.beginPath(); c.arc(flash.x - cam.x, groundY - 4, 14, 0, 7); c.stroke();
    }
    const frame = walkT > 0 ? Math.floor(walkT) % 4 : 0;
    drawSprite(c, ctx.avatar, body.x + body.w / 2 - cam.x, body.y + body.h - cam.y, body.h / 32, frame, facing);
  }

  const loop = makeLoop(update, draw);
  newProblem();
  loop.start();
  return { destroy() { loop.stop(); pad.destroy(); S.destroy(); clear(host); } };
}

const mod: GameModule = {
  id: 'math.number-line-jumper',
  title: 'Number Line Jumper',
  blurb: 'Walk the number line and hop onto the right number. Negatives and fractions later.',
  category: 'math',
  skills: ['mult_div', 'add_sub', 'fractions'],
  levelRange: [1, 10],
  unlock: { stars: 13 },
  mount,
};
export default mod;
